import { currencyToSymbol } from '@src/utils/pricing';
import { addMonths } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'theme-ui';

type selectedDatesProps = {
  selectedTimes: { date: string; hour: string }[];
  setSelectedTimes: (value: any) => void;
  lessonCount: number;
  price: number;
  currency: string;
  selectedDiscount: number;
  priceWithDiscount: number;
  discountOptions: { discount: number; smallBoxBackground: string }[];
  setShowSelectedDates: (value: any) => void;
  t: any;
  i18n: any;
};
export function checkIfLessonsWithinRange(lessonCount, selectedTimes) {
  if (lessonCount !== selectedTimes.length) return true;
  const firstDate = new Date(selectedTimes[0].date);
  const lastDate = new Date(selectedTimes[selectedTimes.length - 1].date);
  const allowedLastDate = addMonths(firstDate, lessonCount <= 8 ? 2 : 3);
  return allowedLastDate > lastDate;
}
function SelectedDates({
  selectedTimes,
  setSelectedTimes,
  lessonCount,
  price,
  currency,
  selectedDiscount,
  priceWithDiscount,
  discountOptions,
  setShowSelectedDates,
}: selectedDatesProps) {
  const isLessonsWithinRange = checkIfLessonsWithinRange(
    lessonCount,
    selectedTimes
  );
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ fontSize: '20px' }}>
      <Box
        sx={{
          color: '#5388FF',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
        }}
        onClick={() => setShowSelectedDates(false)}
      >
        &lt; {t('goBack')}
      </Box>
      <Box
        sx={{
          margin: '30px 0',
          '@media screen and (max-width: 52em)': {
            fontSize: '16px',
          },
        }}
      >
        {selectedTimes?.map(({ date, hour }, index) => {
          let formattedLessonTime = new Date(date).toLocaleDateString(
            i18n.language,
            {
              month: 'long',
              weekday: 'long',
              day: 'numeric',
            }
          );
          formattedLessonTime =
            formattedLessonTime[0].toUpperCase() + formattedLessonTime.slice(1);
          return (
            <Box
              key={date + hour}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ color: index >= lessonCount ? 'red' : 'black' }}>
                {formattedLessonTime}, {hour}.00
              </Box>
              <Box
                sx={{
                  background: '#F29D9D ',
                  borderRadius: '50%',
                  width: '21px',
                  height: '21px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  setSelectedTimes(
                    selectedTimes.filter(
                      (selectedTime) =>
                        selectedTime.date !== date || selectedTime.hour !== hour
                    )
                  )
                }
              >
                {' '}
                -{' '}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          borderRadius: '14px 14px 0px 0px',
          background: '#F2F6FF',
          color: '#ACACAC',
          padding: '15px 20px 10px 20px',
          display: 'flex',
          gap: '5px',
          alignItems: 'center',
          height: '52px',
          '@media screen and (max-width: 52em)': {
            fontSize: '18px',
          },
        }}
      >
        <Box> {t('total')}:</Box>
        <Box
          sx={{
            fontWeight: selectedDiscount ? '' : 'bold',
            color: selectedDiscount ? 'grey600' : 'black',
            textDecoration: selectedDiscount ? 'line-through' : '',
          }}
        >
          {(price * lessonCount).toFixed(2)} {currencyToSymbol[currency]}
        </Box>

        {selectedDiscount ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              sx={{
                fontWeight: 'bold',
                color: 'black',
              }}
            >
              {priceWithDiscount * lessonCount} {currencyToSymbol[currency]}
            </Box>
            <Box
              sx={{
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: 'black',
                width: '38px',
                height: '38px',
                background: discountOptions.find(
                  (item) => item.discount === selectedDiscount
                )?.smallBoxBackground,
                '@media screen and (max-width: 52em)': {
                  fontSize: '14px',
                },
              }}
            >
              {'-' + selectedDiscount + '%'}
            </Box>
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{
          borderRadius: '0px 0px 14px 14px ',
          background: '#C4EFD6',
          fontWeight: 'bold',
          padding: '10px 20px 15px 20px',
          height: '52px',
          '@media screen and (max-width: 52em)': {
            fontSize: '18px',
          },
        }}
      >
        {t('youSave')}:{' '}
        {selectedDiscount
          ? (price * lessonCount - priceWithDiscount * lessonCount).toFixed(2)
          : '0.00'}{' '}
        {currencyToSymbol[currency]}
      </Box>
      {lessonCount !== selectedTimes?.length && (
        <Box
          sx={{
            textAlign: 'center',
            color: 'red',
            marginTop: '50px',
            fontSize: '16px',
          }}
        >
          {t('lessonsAndTypesCountError')}
        </Box>
      )}
      {!isLessonsWithinRange && (
        <Box
          sx={{
            textAlign: 'center',
            color: 'red',
            marginTop: '50px',
            fontSize: '16px',
          }}
        >
          {t('lessonNotInRange.lessonsHasToBe')}
          {lessonCount <= 8 ? 2 : 3}
          {t('lessonNotInRange.monthsRange')}
        </Box>
      )}
    </Box>
  );
}

export default SelectedDates;
